import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { ThumbsUp, X, MessageCircle, MapPin, Clock, Users, Star, Flame, Heart, LayoutGrid, List, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { getEventFeed, upvoteEvent, passEvent, joinEventChat } from '../lib/api';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';

export default function FeedPage() {
  const [events, setEvents] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [joinDialog, setJoinDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('hw_view_mode') || 'swipe';
  });
  const navigate = useNavigate();

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-25, 25]);
  const opacity = useTransform(x, [-200, -100, 0, 100, 200], [0.5, 1, 1, 1, 0.5]);
  const upvoteOpacity = useTransform(x, [0, 100], [0, 1]);
  const passOpacity = useTransform(x, [-100, 0], [1, 0]);
  const joinOpacity = useTransform(y, [-100, 0], [1, 0]);

  useEffect(() => {
    loadFeed();
  }, []);

  useEffect(() => {
    localStorage.setItem('hw_view_mode', viewMode);
  }, [viewMode]);

  const loadFeed = async () => {
    try {
      const res = await getEventFeed();
      setEvents(res.data);
      // Find first non-swiped event for swipe view
      const firstUnswiped = res.data.findIndex(e => e.user_status === 'not_swiped');
      setCurrentIndex(firstUnswiped >= 0 ? firstUnswiped : res.data.length);
    } catch (error) {
      toast.error('Failed to load events');
    }
    setLoading(false);
  };

  const currentEvent = events[currentIndex];

  const handleSwipe = useCallback(async (direction, event = currentEvent) => {
    if (!event) return;
    
    const xTarget = direction === 'right' ? 500 : direction === 'left' ? -500 : 0;
    const yTarget = direction === 'up' ? -500 : 0;
    
    if (viewMode === 'swipe') {
      await animate(x, xTarget, { type: 'spring', stiffness: 300, damping: 20 });
      if (direction === 'up') {
        await animate(y, yTarget, { type: 'spring', stiffness: 300, damping: 20 });
      }
    }

    try {
      if (direction === 'right') {
        await upvoteEvent(event.event_id);
        toast.success('Added to interested! 💖');
        // Update local state
        setEvents(prev => prev.map(e => 
          e.event_id === event.event_id ? { ...e, user_status: 'interested', is_upvoted: true } : e
        ));
      } else if (direction === 'left') {
        await passEvent(event.event_id);
        toast('Not interested');
        setEvents(prev => prev.map(e => 
          e.event_id === event.event_id ? { ...e, user_status: 'not_interested' } : e
        ));
      } else if (direction === 'up') {
        setSelectedEvent(event);
        setJoinDialog(true);
        x.set(0);
        y.set(0);
        return;
      }
    } catch (error) {
      toast.error('Action failed');
    }

    if (viewMode === 'swipe') {
      setTimeout(() => {
        setCurrentIndex((prev) => prev + 1);
        x.set(0);
        y.set(0);
      }, 100);
    }
  }, [currentEvent, x, y, viewMode]);

  const handleDragEnd = (event, info) => {
    const swipeThreshold = 100;
    if (info.offset.x > swipeThreshold) {
      handleSwipe('right');
    } else if (info.offset.x < -swipeThreshold) {
      handleSwipe('left');
    } else if (info.offset.y < -swipeThreshold) {
      handleSwipe('up');
    } else {
      animate(x, 0, { type: 'spring', stiffness: 300, damping: 20 });
      animate(y, 0, { type: 'spring', stiffness: 300, damping: 20 });
    }
  };

  const confirmJoinChat = async () => {
    const event = selectedEvent || currentEvent;
    try {
      await joinEventChat(event.event_id);
      toast.success('Joined chat! 🎉');
      setJoinDialog(false);
      navigate(`/chat/${event.event_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'interested':
        return <Badge className="bg-pink-100 text-pink-600 border-0">💖 Interested</Badge>;
      case 'not_interested':
        return <Badge className="bg-gray-100 text-gray-500 border-0">Skipped</Badge>;
      default:
        return <Badge className="bg-blue-100 text-blue-600 border-0">New</Badge>;
    }
  };

  // Filter events for swipe view (only not_swiped)
  const swipeEvents = events.filter(e => e.user_status === 'not_swiped');
  const swipeCurrentEvent = swipeEvents[0];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-pink-500 animate-pulse mx-auto mb-4" />
          <p className="text-gray-500">Loading vibes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col pb-20">
      {/* Header */}
      <header className="p-4 flex items-center justify-between bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <h1 className="text-2xl font-bold text-pink-500">HW Vibes</h1>
        <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl">
          <Button
            variant="ghost"
            size="sm"
            className={`h-8 px-3 rounded-lg ${viewMode === 'swipe' ? 'bg-white shadow-sm text-pink-500' : 'text-gray-500'}`}
            onClick={() => setViewMode('swipe')}
            data-testid="swipe-view-btn"
          >
            <LayoutGrid className="w-4 h-4 mr-1" />
            Swipe
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className={`h-8 px-3 rounded-lg ${viewMode === 'list' ? 'bg-white shadow-sm text-pink-500' : 'text-gray-500'}`}
            onClick={() => setViewMode('list')}
            data-testid="list-view-btn"
          >
            <List className="w-4 h-4 mr-1" />
            List
          </Button>
        </div>
      </header>

      {viewMode === 'list' ? (
        /* LIST VIEW */
        <div className="flex-1 overflow-y-auto px-4 space-y-3 pt-2 pb-24">
          {events.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[60vh] text-center">
              <div className="w-20 h-20 rounded-full bg-pink-100 flex items-center justify-center mb-6">
                <Heart className="w-10 h-10 text-pink-400" />
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">No events yet</h2>
              <p className="text-gray-500">Check back soon for new vibes!</p>
            </div>
          ) : (
            events.map((event, idx) => (
              <motion.div
                key={event.event_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
              >
                <div className="flex">
                  {/* Image */}
                  <img
                    src={event.image_url}
                    alt={event.title}
                    className="w-28 h-28 object-cover cursor-pointer"
                    onClick={() => navigate(`/event/${event.event_id}`)}
                  />
                  
                  {/* Content */}
                  <div className="flex-1 p-3 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 
                        className="font-semibold text-gray-800 truncate cursor-pointer hover:text-pink-500"
                        onClick={() => navigate(`/event/${event.event_id}`)}
                      >
                        {event.title}
                      </h3>
                      {getStatusBadge(event.user_status)}
                    </div>
                    
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                      <Clock className="w-3 h-3" />
                      <span>{formatDate(event.event_date)}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                      <MapPin className="w-3 h-3" />
                      <span className="truncate">{event.location}</span>
                    </div>
                    
                    <p className="text-xs text-gray-400 line-clamp-1 mb-2">
                      {event.description}
                    </p>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {event.user_status !== 'interested' && (
                        <Button
                          size="sm"
                          className="h-7 px-3 bg-pink-500 hover:bg-pink-600 text-xs rounded-lg"
                          onClick={() => handleSwipe('right', event)}
                          data-testid={`interested-btn-${event.event_id}`}
                        >
                          <Heart className="w-3 h-3 mr-1" />
                          Interested
                        </Button>
                      )}
                      {event.user_status === 'not_swiped' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-3 text-xs rounded-lg border-gray-200"
                          onClick={() => handleSwipe('left', event)}
                          data-testid={`skip-btn-${event.event_id}`}
                        >
                          <X className="w-3 h-3 mr-1" />
                          Skip
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-3 text-xs rounded-lg border-gray-200 text-blue-500"
                        onClick={() => {
                          setSelectedEvent(event);
                          setJoinDialog(true);
                        }}
                        data-testid={`join-btn-${event.event_id}`}
                      >
                        <MessageCircle className="w-3 h-3 mr-1" />
                        Join
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 ml-auto"
                        onClick={() => navigate(`/event/${event.event_id}`)}
                      >
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      ) : (
        /* SWIPE VIEW */
        <>
          {!swipeCurrentEvent ? (
            <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
              <div className="w-20 h-20 rounded-full bg-pink-100 flex items-center justify-center mb-6">
                <Heart className="w-10 h-10 text-pink-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">You&apos;re all caught up!</h2>
              <p className="text-gray-500 mb-4">No more events to swipe</p>
              <Button onClick={() => setViewMode('list')} className="bg-pink-500 hover:bg-pink-600 border-0 rounded-xl">
                View All Events
              </Button>
            </div>
          ) : (
            <>
              {/* Card Stack */}
              <div className="flex-1 relative px-4 pb-2" style={{ minHeight: '50vh', zIndex: 1 }}>
                <div className="absolute inset-4 card-stack" style={{ zIndex: 1 }}>
                  {/* Background cards */}
                  {swipeEvents.slice(1, 3).map((event, i) => (
                    <div
                      key={event.event_id}
                      className="absolute inset-0 rounded-3xl overflow-hidden border border-gray-200 shadow-lg"
                      style={{
                        transform: `scale(${0.95 - i * 0.05}) translateY(${(i + 1) * 12}px)`,
                        opacity: 0.7 - i * 0.3,
                      }}
                    >
                      <img src={event.image_url} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}

                  {/* Active card */}
                  <motion.div
                    data-testid="event-card"
                    className="absolute inset-0 rounded-3xl overflow-hidden shadow-2xl border border-gray-100 no-select touch-none bg-white"
                    style={{ x, y, rotate, opacity }}
                    drag
                    dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
                    dragElastic={0.7}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="relative h-full">
                      <img
                        src={swipeCurrentEvent.image_url}
                        alt={swipeCurrentEvent.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

                      {/* Swipe indicators */}
                      <motion.div
                        className="absolute top-8 left-8 px-6 py-3 rounded-2xl bg-pink-500 text-white font-bold text-xl rotate-[-20deg] shadow-lg"
                        style={{ opacity: upvoteOpacity }}
                      >
                        LOVE IT 💖
                      </motion.div>
                      <motion.div
                        className="absolute top-8 right-8 px-6 py-3 rounded-2xl bg-gray-700 text-white font-bold text-xl rotate-[20deg] shadow-lg"
                        style={{ opacity: passOpacity }}
                      >
                        SKIP ✕
                      </motion.div>
                      <motion.div
                        className="absolute top-8 left-1/2 -translate-x-1/2 px-6 py-3 rounded-2xl bg-blue-500 text-white font-bold text-xl shadow-lg"
                        style={{ opacity: joinOpacity }}
                      >
                        JOIN 💬
                      </motion.div>

                      {/* Trending badge */}
                      {swipeCurrentEvent.is_trending && (
                        <div className="absolute top-4 left-4">
                          <Badge className="bg-pink-500 text-white border-0 trending-badge">
                            <Flame className="w-3 h-3 mr-1" />
                            Hot
                          </Badge>
                        </div>
                      )}

                      {/* Content */}
                      <div className="absolute bottom-0 left-0 right-0 p-6 space-y-3">
                        <Badge className="bg-white/20 backdrop-blur-sm text-white border-0">
                          {swipeCurrentEvent.category}
                        </Badge>

                        <h2 className="text-3xl font-black text-white tracking-tight">
                          {swipeCurrentEvent.title}
                        </h2>

                        <div className="flex flex-wrap gap-3 text-white/90 text-sm">
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatDate(swipeCurrentEvent.event_date)}
                          </div>
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {swipeCurrentEvent.location}
                          </div>
                        </div>

                        <div className="flex items-center gap-4 pt-2">
                          <div className="flex items-center gap-1 text-white/80">
                            <Heart className="w-4 h-4 fill-pink-400 text-pink-400" />
                            <span className="font-semibold">{swipeCurrentEvent.upvotes}</span>
                          </div>
                          <div className="flex items-center gap-1 text-white/80">
                            <Users className="w-4 h-4" />
                            <span className="font-semibold">{swipeCurrentEvent.chat_members}</span>
                          </div>
                          {swipeCurrentEvent.average_rating > 0 && (
                            <div className="flex items-center gap-1 text-white/80">
                              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                              <span className="font-semibold">{swipeCurrentEvent.average_rating.toFixed(1)}</span>
                            </div>
                          )}
                          <div className="ml-auto px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm">
                            <span className="text-sm font-medium text-white">
                              {swipeCurrentEvent.fee > 0 ? `£${swipeCurrentEvent.fee.toFixed(2)}` : 'Free'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="p-4 flex justify-center gap-5 pb-4">
                <Button
                  data-testid="pass-btn"
                  variant="outline"
                  size="icon"
                  className="w-16 h-16 rounded-full border-2 border-gray-300 text-gray-400 hover:bg-gray-100 hover:text-gray-600 hover:border-gray-400 shadow-md"
                  onClick={() => handleSwipe('left')}
                >
                  <X className="w-8 h-8" />
                </Button>
                <Button
                  data-testid="join-btn"
                  size="icon"
                  className="w-16 h-16 rounded-full bg-blue-500 hover:bg-blue-600 shadow-lg border-0"
                  onClick={() => handleSwipe('up')}
                >
                  <MessageCircle className="w-8 h-8 text-white" />
                </Button>
                <Button
                  data-testid="upvote-btn"
                  size="icon"
                  className="w-16 h-16 rounded-full bg-pink-500 hover:bg-pink-600 shadow-lg border-0"
                  onClick={() => handleSwipe('right')}
                >
                  <Heart className="w-8 h-8 text-white" />
                </Button>
              </div>
            </>
          )}
        </>
      )}

      {/* Join Dialog */}
      <Dialog open={joinDialog} onOpenChange={setJoinDialog}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-pink-500">Join Chat Group?</DialogTitle>
          </DialogHeader>
          <p className="text-gray-500">
            Join the chat for <span className="font-semibold text-gray-800">{selectedEvent?.title || currentEvent?.title}</span>?
          </p>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setJoinDialog(false)} className="rounded-xl">Cancel</Button>
            <Button data-testid="confirm-join-btn" onClick={confirmJoinChat} className="bg-pink-500 hover:bg-pink-600 border-0 rounded-xl">Join Chat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
}
